import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "../api";
import SEO from "../components/SEO";
import { useLanguage } from "../context/LanguageContext";
import PageContent from "../sections/PageContent";
import { useSiteTheme } from "../theme/ThemeContext";

import type { FormEvent, ChangeEvent } from "react";

interface ContactContent {
  contact_title_en: string;
  contact_title_ro: string;
  contact_content_en: string;
  contact_content_ro: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  contact_hours: string;
}

interface ContactFormData {
  name: string;
  email: string;
  message: string;
}

interface ArtworkEnquiry {
  type: "artwork";
  artworkId: number;
  catalogueNumber: string | null;
  artworkTitle: string;
}

interface CommissionEnquiry {
  type: "commission";
}

interface EnquiryLocationState {
  enquiry?: ArtworkEnquiry | CommissionEnquiry;
}

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

function Contact() {
  const location = useLocation();
  const { language } = useLanguage();
  const enquiry = (location.state as EnquiryLocationState | null)?.enquiry;
  const [content, setContent] = useState<ContactContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const { palette } = useSiteTheme();

  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    if (!enquiry) return;

    if (enquiry.type === "commission") {
      setFormData((prev) => ({
        ...prev,
        message:
          "I would like to enquire about commissioning an icon.\n\n" +
          "Please could you provide further information about the commissioning process?",
      }));

      return;
    }

    const catalogueText = enquiry.catalogueNumber
      ? `Catalogue No: ${enquiry.catalogueNumber}`
      : "Catalogue number unavailable";

    setFormData((prev) => ({
      ...prev,
      message:
        `I would like to enquire about "${enquiry.artworkTitle}".\n\n` +
        `${catalogueText}\n\n` +
        "Please could you provide further information about this artwork?",
    }));
  }, [enquiry]);

  const title =
    language === "ro" ? content?.contact_title_ro : content?.contact_title_en;

  const contactText =
    language === "ro"
      ? content?.contact_content_ro
      : content?.contact_content_en;

  useEffect(() => {
    const fetchContactContent = async (): Promise<void> => {
      try {
        setLoading(true);

        const response = await api.get<ContactContent>("/content");

        setContent(response.data);
        setError("");
      } catch (error) {
        console.error("Error fetching contact content:", error);
        setError("Failed to load contact content");
        setContent(null);
      } finally {
        setLoading(false);
      }
    };

    void fetchContactContent();
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setSubmitting(true);
    setSubmitError("");

    try {
      await api.post("/contact", formData);

      setSubmitSuccess(true);

      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Failed to send enquiry:", error);
      setSubmitError(
        "Sorry, your message could not be sent. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Contact structured data
  const contactStructuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    mainEntity: {
      "@type": "Organization",
      name: "Byzantine Art Studio",
      url: "https://byzantineartstudio.co.uk",
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "Customer Service",
        email: content?.contact_email || "",
      },
    },
  };

  return (
    <>
      <SEO
        title={title || "Contact"}
        description={
          contactText
            ? contactText.substring(0, 160)
            : "Contact the artist about an artwork or commission."
        }
        url="/contact"
        keywords="contact Byzantine Art Studio, hand painted icons, icon inquiries, icon orders, icon commissions"
        structuredData={contactStructuredData}
      />
      <PageContent bg={palette.hero}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Paper
            sx={{
              p: 4,
              bgcolor: palette.paper,
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Typography variant="h4" component="h1" gutterBottom>
              {title || "Contact"}
            </Typography>
            <Box sx={{ mt: 4 }}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Get in Touch
                  </Typography>
                  {contactText &&
                    contactText.split("\n").map((paragraph, index) => (
                      <Typography key={index} variant="body1" paragraph>
                        {paragraph}
                      </Typography>
                    ))}
                  {content?.contact_email && (
                    <Typography variant="body1" paragraph>
                      Email: {content.contact_email}
                    </Typography>
                  )}
                  {(content?.contact_address ||
                    content?.contact_phone ||
                    content?.contact_hours) && (
                    <Typography
                      variant="body1"
                      paragraph
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      {content.contact_address
                        ? `Studio Address:\n${content.contact_address}`
                        : ""}

                      {content.contact_phone
                        ? `\nPhone: ${content.contact_phone}`
                        : ""}

                      {content.contact_hours
                        ? `\nHours: ${content.contact_hours}`
                        : ""}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <form onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      margin="normal"
                      required
                    />
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      margin="normal"
                      required
                    />
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      margin="normal"
                      required
                    />
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      margin="normal"
                      required
                      sx={{
                        "& textarea": {
                          scrollbarColor: `${palette.navbarText} ${palette.paper}`,
                          scrollbarWidth: "thin",

                          "&::-webkit-scrollbar": {
                            width: "8px",
                          },

                          "&::-webkit-scrollbar-track": {
                            backgroundColor: palette.paper,
                          },

                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: palette.navbarText,
                            borderRadius: "4px",
                          },
                        },
                      }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting}
                      sx={{
                        mt: 2,
                        bgcolor: palette.primary,
                        color: "#fff",
                        "&:hover": {
                          bgcolor: palette.secondary,
                        },
                      }}
                    >
                      {submitting ? (
                        <>
                          <CircularProgress
                            size={20}
                            color="inherit"
                            sx={{ mr: 1 }}
                          />
                          Sending...
                        </>
                      ) : (
                        "Send Enquiry"
                      )}
                    </Button>
                  </form>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Container>
      </PageContent>
      <Snackbar
        open={submitSuccess}
        autoHideDuration={5000}
        onClose={() => setSubmitSuccess(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSubmitSuccess(false)}
          sx={{ width: "100%" }}
        >
          Thank you for your enquiry. Your message has been sent successfully.
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(submitError)}
        autoHideDuration={6000}
        onClose={() => setSubmitError("")}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setSubmitError("")}
          sx={{ width: "100%" }}
        >
          {submitError}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Contact;
